import { useEffect, useState } from "react";
import { playPop } from "../../lib/sounds";
import { ButtonPress } from "../Button/ButtonPress";
import { Tooltip } from "../Tooltip";
import { NudgeTooltipContent } from "../NudgeTooltipContent";

const NUDGE_DURATION_MS = 5_000;
const SEEN_WORKFLOWS_PREFIX = "tidy-up-seen-workflows:";

function claimUnseenWorkflows(
  spaceId: string,
  workflowIdsKey: string
): boolean {
  if (typeof localStorage === "undefined") return true;
  const key = `${SEEN_WORKFLOWS_PREFIX}${spaceId}`;
  const workflowIds = JSON.parse(workflowIdsKey) as string[];
  const seenWorkflowIds = new Set<string>(
    JSON.parse(localStorage.getItem(key) ?? "[]")
  );
  if (workflowIds.every((id) => seenWorkflowIds.has(id))) return false;
  for (const id of workflowIds) seenWorkflowIds.add(id);
  localStorage.setItem(key, JSON.stringify([...seenWorkflowIds]));
  return true;
}

type Props = {
  spaceId: string;
  workflowIds: readonly string[];
  onPress: () => void;
};

export function TidyUpButton({ spaceId, workflowIds, onPress }: Props) {
  const [nudgeVisible, setNudgeVisible] = useState(false);
  const workflowIdsKey = JSON.stringify([...workflowIds].sort());

  useEffect(() => {
    const showNudge = () => {
      if (!claimUnseenWorkflows(spaceId, workflowIdsKey)) {
        // oxlint-disable-next-line react/react-compiler -- EffectSetState: visibility follows the persisted set of seen workflows
        setNudgeVisible(false);
        return;
      }
      playPop();
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: visibility follows the persisted set of seen workflows
      setNudgeVisible(true);
    };
    if (document.visibilityState === "visible") {
      showNudge();
      return;
    }
    const showWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", showWhenVisible);
      showNudge();
    };
    document.addEventListener("visibilitychange", showWhenVisible);
    return () =>
      document.removeEventListener("visibilitychange", showWhenVisible);
  }, [spaceId, workflowIdsKey]);

  useEffect(() => {
    if (!nudgeVisible) return;
    const hide = setTimeout(() => setNudgeVisible(false), NUDGE_DURATION_MS);
    return () => clearTimeout(hide);
  }, [nudgeVisible, spaceId]);

  const noun = workflowIds.length === 1 ? "workflow" : "workflows";
  const message = `You’ve got ${workflowIds.length} unused ${noun} you may want to delete. Take a look.`;
  const content = nudgeVisible ? (
    <NudgeTooltipContent>{message}</NudgeTooltipContent>
  ) : (
    "Tidy up"
  );

  return (
    <Tooltip
      align={nudgeVisible ? "right" : undefined}
      content={content}
      forceOpen={nudgeVisible}
      vertical={nudgeVisible ? "below" : undefined}
    >
      <ButtonPress
        aria-label="Tidy up"
        variant="secondary"
        icon="brush-cleaning"
        onPress={() => {
          setNudgeVisible(false);
          onPress();
        }}
      />
    </Tooltip>
  );
}
